import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Loader2, Copy, Check } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

const ACCENT = { basic: "#2AB7CA", standard: "#FF5A79", premium: "#9B5DE5" };

export default function GiftPanel() {
  const [options, setOptions] = useState({ plans: [], durations: [] });
  const [gifts, setGifts] = useState([]);
  const [plan, setPlan] = useState("standard");
  const [months, setMonths] = useState("3");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(null);
  const [copied, setCopied] = useState(null);

  const loadGifts = () => api.get("/gifts").then(({ data }) => setGifts(data)).catch(() => {});
  useEffect(() => {
    api.get("/gifts/options").then(({ data }) => setOptions(data)).catch(() => {});
    loadGifts();
  }, []);

  const selectedPlan = options.plans.find((p) => p.id === plan);
  const total = selectedPlan ? (selectedPlan.price * Number(months)).toFixed(2) : "0.00";

  const payload = () => ({ plan, months: Number(months), recipient_email: email, message, origin_url: window.location.origin });

  const buyGift = async () => {
    setBusy("buy");
    try {
      const { data } = await api.post("/gifts/checkout", payload());
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const generateFree = async () => {
    setBusy("gen");
    try {
      await api.post("/gifts/generate", payload());
      toast.success("Gift code generated 🎁");
      setEmail(""); setMessage("");
      loadGifts();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const copy = (code) => {
    navigator.clipboard?.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 1500);
  };

  const statusLabel = { unredeemed: "Ready to gift", redeemed: "Redeemed", pending: "Payment pending" };
  const statusColor = { unredeemed: "bg-emerald-100 text-emerald-700", redeemed: "bg-slate-200 text-slate-500", pending: "bg-amber-100 text-amber-700" };

  return (
    <div className="lg:col-span-3 bg-white rounded-3xl border border-amber-100/60 shadow-lg p-6" data-testid="gift-panel">
      <div className="flex items-center gap-3 mb-5">
        <span className="h-12 w-12 rounded-2xl bg-[#9B5DE5] text-white flex items-center justify-center shrink-0"><Gift className="h-6 w-6" /></span>
        <div>
          <h2 className="text-xl font-display font-bold text-slate-900">Gift Bubz <span className="text-[#FF5A79]">+</span></h2>
          <p className="text-sm text-slate-500">Buy a plan for another family — they redeem a code, no card needed.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Form */}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-slate-500">Plan</label>
              <Select value={plan} onValueChange={setPlan}>
                <SelectTrigger data-testid="gift-plan-select" className="rounded-xl h-11 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {options.plans.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`gift-plan-${p.id}`}>{p.name} · ${p.price}/mo</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500">Duration</label>
              <Select value={months} onValueChange={setMonths}>
                <SelectTrigger data-testid="gift-months-select" className="rounded-xl h-11 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {options.durations.map((d) => (
                    <SelectItem key={d.months} value={String(d.months)} data-testid={`gift-months-${d.months}`}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Input data-testid="gift-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Recipient email (optional)" className="rounded-xl h-11" />
          <Input data-testid="gift-message-input" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Add a short message (optional)" className="rounded-xl h-11" />
          <div className="flex items-center justify-between rounded-xl bg-[#9B5DE5]/5 border border-[#9B5DE5]/20 px-4 py-3">
            <span className="text-sm font-semibold text-slate-600">Total</span>
            <span className="text-xl font-display font-bold" style={{ color: ACCENT[plan] }} data-testid="gift-total">${total}</span>
          </div>
          <div className="flex gap-2">
            <Button data-testid="gift-buy-btn" onClick={buyGift} disabled={busy !== null}
              className="flex-1 rounded-xl h-11 font-bold bg-[#9B5DE5] hover:bg-[#8349c9] text-white">
              {busy === "buy" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Buy gift"}
            </Button>
            <Button data-testid="gift-generate-btn" onClick={generateFree} disabled={busy !== null} variant="outline"
              className="rounded-xl h-11 font-bold border-2">
              {busy === "gen" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate free code"}
            </Button>
          </div>
        </div>

        {/* Codes list */}
        <div>
          <h3 className="text-sm font-bold text-slate-500 mb-2">Your gift codes</h3>
          {gifts.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-slate-200 p-6 text-center text-sm text-slate-400" data-testid="gift-empty">
              No gift codes yet. Buy or generate one to share!
            </div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="gift-list">
              {gifts.map((g) => (
                <div key={g.gift_id} data-testid={`gift-item-${g.gift_id}`} className="rounded-xl border border-slate-100 p-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-mono text-sm font-bold text-slate-900 truncate">{g.code}</div>
                    <div className="text-xs text-slate-400">{g.plan_name} · {g.months} mo</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${statusColor[g.status] || "bg-slate-100 text-slate-500"}`}>{statusLabel[g.status] || g.status}</span>
                    {g.status !== "pending" && (
                      <button data-testid={`gift-copy-${g.gift_id}`} onClick={() => copy(g.code)} className="text-slate-400 hover:text-[#9B5DE5]">
                        {copied === g.code ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
